/**
 * Updates the recently viewed products in localStorage.
 */
export class RecentlyViewed {
  /** @static @constant {string} The key used to store the viewed products in session storage */
  static #STORAGE_KEY = 'viewedProducts';
  /** @static @constant {number} The maximum number of products to store */
  static #MAX_PRODUCTS = 8;

  /**
   * Adds a product to the recently viewed products list.
   * @param {string} productId - The ID of the product to add.
   */
  static addProduct(productId) {
    let viewedProducts = this.getProducts();

    viewedProducts = viewedProducts.filter((/** @type {string} */ id) => id !== productId);
    viewedProducts.unshift(productId);
    viewedProducts = viewedProducts.slice(0, this.#MAX_PRODUCTS);

    localStorage.setItem(this.#STORAGE_KEY, JSON.stringify(viewedProducts));
  }

  static clearProducts() {
    localStorage.removeItem(this.#STORAGE_KEY);
  }

  /**
   * Retrieves the list of recently viewed products from session storage.
   * @returns {string[]} The list of viewed products.
   */
  static getProducts() {
    return JSON.parse(localStorage.getItem(this.#STORAGE_KEY) || '[]');
  }
}

/**
 * Custom element that fetches and renders the recently viewed products section.
 * Reads from the RecentlyViewed utility class already used by predictive search.
 */
class RecentlyViewedProductsElement extends HTMLElement {
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;
      observer.disconnect();
      this.#load();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /** @type {Record<string, string>} */
  #cache = {};

  connectedCallback() {
    this.#intersectionObserver.observe(this);
  }

  disconnectedCallback() {
    this.#intersectionObserver.disconnect();
  }

  #buildUrl() {
    const { currentProductId, maxProducts, url } = this.dataset;
    const max = parseInt(maxProducts || '4', 10);

    let ids = [];
    try {
      const raw = localStorage.getItem('viewedProducts');
      ids = JSON.parse(raw) || [];
    } catch {
      ids = [];
    }

    if (currentProductId) {
      ids = ids.filter((id) => id !== currentProductId);
    }

    if (!ids.length) return null;

    ids = ids.slice(0, max);

    // Use Shopify search with id: filters — same approach as predictive-search.js
    // This is the only reliable way to pass product IDs into Liquid via section rendering
    const searchQuery = ids.map((id) => `id:${id}`).join(' OR ');
    const sectionId = url.split('sections=')[1];

    return `/search?q=${encodeURIComponent(searchQuery)}&type=product&limit=${max}&sections=${sectionId}`;
  }

  async #load() {
    const id = this.id;
    const url = this.#buildUrl();

    if (!url) {
      this.dataset.empty = 'true';
      return;
    }

    if (this.#cache[url]) {
      this.#inject(this.#cache[url], id);
      return;
    }

    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`Server returned ${response.status}`);
      const text = await response.text();
      this.#cache[url] = text;
      this.#inject(text, id);
    } catch (err) {
      console.error('Recently viewed products error:', err);
      this.dataset.empty = 'true';
    }
  }

  /** @param {string} html @param {string} id */
  #inject(rawResponse, id) {
    let html = rawResponse;

    try {
      const json = JSON.parse(rawResponse);
      const sectionId = id.replace('recently-viewed-products-', '');
      html = json[sectionId] ?? Object.values(json)[0];
    } catch {
      // Not JSON, use raw response as-is
    }

    const doc = document.createElement('div');
    doc.innerHTML = html;

    const section = doc.querySelector(`recently-viewed-products[id="${id}"]`);
    if (section?.innerHTML?.trim()) {
      this.innerHTML = section.innerHTML;

      // Re-dispatch a custom event so carousel JS re-initialises
      // on the newly injected HTML
      this.dispatchEvent(new CustomEvent('recently-viewed:injected', { bubbles: true }));

      // Re-upgrade any custom elements inside the injected HTML
      // by cloning and reinserting the carousel elements
      this.querySelectorAll('resource-list-carousel').forEach((carousel) => {
        const clone = carousel.cloneNode(true);
        carousel.replaceWith(clone);
      });
    } else {
      this.dataset.empty = 'true';
    }
  }
}

if (!customElements.get('recently-viewed-products')) {
  customElements.define('recently-viewed-products', RecentlyViewedProductsElement);
}